import React, { useState } from 'react';
import { MoreHorizontal } from 'lucide-react';

import { Button } from 'components/ui/button';
import { NavSheet } from './NavSheet';
import { useMobileFrameContainer } from './MobileFrameContext';
import { NAV_ITEMS, type NavItemData } from './sidebarNavItems';
import { conTextos, TextosDeNavegacion } from './navTextos';

/**
 * Recreación de MobileBottomNavV2.tsx (ya mergeado a master, chirigota
 * Release 9.2.0) — pero SOLO en su parte de navegación. A petición expresa
 * (Elena, 3 sep 2026) plan y rol NO viven aquí: van en `MobileTopBar`
 * (avatar = cuenta/rol, nombre de plan = selector de plan), igual que en
 * escritorio (`SidebarUserMenu`/`SidebarPlanSelector` separados). El real
 * `MobileBottomNavV2.tsx` funde todo (usuario+plan+rol+navegación) en un
 * único avatar-trigger — aquí se aparta a propósito de eso: "Más" es solo
 * las secciones de navegación que no caben, sin avatar ni menú de cuenta
 * duplicando lo que ya hace `MobileTopBar`.
 *
 * Máximo 4 secciones visibles; el resto va a "Más" (icono genérico, no un
 * avatar — el avatar real ya vive arriba) solo si de verdad sobra alguna.
 */

export interface MobileBottomNavProps {
	activeRoute?: string;
	items?: NavItemData[];
	overflowItems?: NavItemData[];
	/** Al pulsar una sección, con su ruta (también las de «Más»): quien monta el bloque es quien navega. */
	onNavigate?: (route: string) => void;
	textos?: Partial<Pick<TextosDeNavegacion, 'mas'>>;
}

export const MobileBottomNav = ({
	activeRoute = 'my-benefit',
	items = NAV_ITEMS,
	overflowItems = [],
	onNavigate,
	textos: textosPropios
}: MobileBottomNavProps) => {
	const textos = conTextos(textosPropios);
	const frameContainer = useMobileFrameContainer();
	const [isOpen, setIsOpen] = useState(false);

	return (
		<>
			<nav className="fixed bottom-0 left-0 right-0 z-50 bg-white/95 dark:bg-sidebar/95 backdrop-blur-lg border-t border-gray-200 dark:border-border safe-area-bottom">
				<div className="flex items-stretch justify-around h-16">
					{items.slice(0, 4).map((item) => {
						const isActive = activeRoute === item.route;
						return (
							<Button
								key={item.route}
								variant="ghost"
								// Por su ruta la encuentran los e2e de la aplicación (`nav-tab-<route>`).
								data-testid={`nav-tab-${item.route}`}
								aria-current={isActive ? 'page' : undefined}
								onClick={() => onNavigate?.(item.route)}
								className={`flex flex-col h-auto items-center justify-center gap-0.5 flex-1 rounded-none py-1.5 transition-colors relative hover:bg-transparent ${
									/* Bug real encontrado con axe: el código real usa text-gray-400
									   para las pestañas inactivas — a 10px da 2.53:1, falla. Aquí se
									   corrige a gray-500 (4.83:1); MobileBottomNavV2.tsx real sigue
									   con gray-400 sin corregir. */
									isActive ? 'text-primary' : 'text-gray-500 dark:text-muted-foreground'
								}`}
							>
								{isActive && (
									<span className="absolute top-0 left-1/2 -translate-x-1/2 w-8 h-0.5 rounded-full bg-primary" />
								)}
								<span
									className={`flex items-center justify-center w-8 h-8 rounded-md ${
										isActive ? 'bg-sidebar-accent' : ''
									}`}
								>
									{item.icon}
								</span>
								<span
									className={`text-[10px] leading-tight font-medium ${isActive ? 'font-semibold' : ''}`}
								>
									{item.label.length > 10 ? `${item.label.slice(0, 8)}...` : item.label}
								</span>
							</Button>
						);
					})}

					{overflowItems.length > 0 && (
						<Button
							variant="ghost"
							onClick={() => setIsOpen(true)}
							className="flex flex-col h-auto items-center justify-center gap-0.5 flex-1 rounded-none py-1.5 text-gray-500 hover:bg-transparent dark:text-muted-foreground"
						>
							<span className="flex items-center justify-center w-8 h-8 rounded-md">
								<MoreHorizontal className="w-5 h-5" />
							</span>
							<span className="text-[10px] leading-tight font-medium">{textos.mas}</span>
						</Button>
					)}
				</div>
			</nav>

			{overflowItems.length > 0 && (
				<NavSheet
					open={isOpen}
					onOpenChange={setIsOpen}
					title={textos.mas}
					container={frameContainer}
				>
					<div className="space-y-1">
						{overflowItems.map((item) => (
							<Button
								key={item.route}
								variant="ghost"
								data-testid={`nav-tab-${item.route}`}
								aria-current={activeRoute === item.route ? 'page' : undefined}
								className="min-h-12 w-full justify-start text-primary hover:bg-sidebar-accent"
								onClick={() => {
									onNavigate?.(item.route);
									setIsOpen(false);
								}}
							>
								{item.icon}
								<span className="ml-3">{item.label}</span>
							</Button>
						))}
					</div>
				</NavSheet>
			)}

			{/* Espaciador para que el contenido no quede debajo de la barra fija */}
			<div className="h-[calc(4rem+env(safe-area-inset-bottom,0px))]" />
		</>
	);
};
